/**
 * The permission catalogue, mirrored from `zapio-backend`.
 *
 * Generated by reading `domain/permission-catalogue.ts` — not transcribed from a document
 * and not inferred from the routes that use them. Verified: **40 names**, which
 * `permission.constant.spec.ts` pins.
 *
 * ## A name invented here can never be granted
 *
 * `hasPermission` is `permissions.has(name)` against the set the backend sent. A string
 * the backend does not define is therefore `false` **forever** — so the control it guards
 * simply never renders, with no error, no log and nothing to notice. A screen that needs a
 * narrower gate is a backend ask, never a new entry here.
 *
 * ## The four high-risk names are separate on purpose
 *
 * Each one reaches a live merchant store, and the backend separates them so a support role
 * can read and prepare without being able to fire them. The UI must not flatten that: a
 * control gated on one of these uses the design system's `high-risk` variant rather than
 * `primary`, and a permission chip the operator does not hold renders with a **dashed**
 * border — the system's consistent "not yours" signal.
 */
export const PERMISSIONS = {
  adminUser: {
    view: 'admin-user-view',
    edit: 'admin-user-edit',
    delete: 'admin-user-delete',
  },
  analytics: {
    view: 'analytics-view',
  },
  app: {
    view: 'app-view',
    edit: 'app-edit',
    delete: 'app-delete',
  },
  appCategory: {
    view: 'app-category-view',
    edit: 'app-category-edit',
    delete: 'app-category-delete',
  },
  auditLog: {
    view: 'audit-log-view',
  },
  // The merchant-side counterpart: what MERCHANTS did, not what operators did. Separate
  // because they answer different questions and a support operator may need one alone.
  shopEvent: {
    view: 'shop-event-view',
  },
  plan: {
    view: 'plan-view',
    edit: 'plan-edit',
    delete: 'plan-delete',
  },
  promotion: {
    view: 'promotion-view',
    edit: 'promotion-edit',
    delete: 'promotion-delete',
    /** HIGH RISK — pushes a discount and its metafields to a live store. */
    publish: 'promotion-publish',
  },
  /** The whole book of business. Deliberately NOT implied by `analytics-view`. */
  revenue: {
    view: 'revenue-view',
  },
  role: {
    view: 'role-view',
    edit: 'role-edit',
    delete: 'role-delete',
  },
  shop: {
    view: 'shop-view',
    edit: 'shop-edit',
    delete: 'shop-delete',
    /** HIGH RISK — the one shop view that reads a credential to answer its question. */
    credentialView: 'shop-credential-view',
  },
  shopifyFunction: {
    view: 'shopify-function-view',
    edit: 'shopify-function-edit',
    delete: 'shopify-function-delete',
    /** HIGH RISK — changes which Function version a shop runs (P1-9 ordering). */
    activate: 'shopify-function-activate',
  },
  subscription: {
    view: 'subscription-view',
    edit: 'subscription-edit',
    delete: 'subscription-delete',
  },
  translation: {
    view: 'translation-view',
    edit: 'translation-edit',
    delete: 'translation-delete',
  },
  widget: {
    view: 'widget-view',
    edit: 'widget-edit',
    delete: 'widget-delete',
    /** HIGH RISK — one row change alters every storefront that inherits the default. */
    editDefault: 'widget-edit-default',
  },
} as const

/** Every name, flattened. The count is asserted by the spec beside this file. */
export const ALL_PERMISSIONS: readonly string[] = Object.values(PERMISSIONS)
  .flatMap((group) => Object.values(group))
  .sort()

export const EXPECTED_PERMISSION_COUNT = 41

/**
 * The four the backend keeps separate because each reaches a live merchant store.
 *
 * Used by the UI to pick the `high-risk` button variant, so "this is dangerous" is a
 * property of the permission rather than a judgement each screen makes again.
 */
export const HIGH_RISK_PERMISSIONS: readonly string[] = [
  PERMISSIONS.promotion.publish,
  PERMISSIONS.shop.credentialView,
  PERMISSIONS.shopifyFunction.activate,
  PERMISSIONS.widget.editDefault,
]

/** A permission name, as a type. A typo in a route's `requires` fails the build. */
type Flatten<T> = T extends Record<string, infer V>
  ? V extends string
    ? V
    : Flatten<V>
  : never

export type PermissionName = Flatten<typeof PERMISSIONS>

/**
 * Human labels for the catalogue, so a screen can present it as **14 modules × their
 * actions** rather than as a wall of 40 slugs.
 *
 * Derived from `PERMISSIONS` rather than retyped: the names exist once, and a module or
 * action added above with no label here fails `permission.constant.spec.ts` instead of
 * rendering a blank chip.
 */
const MODULE_LABELS: Record<keyof typeof PERMISSIONS, string> = {
  adminUser: 'Operators',
  analytics: 'Analytics',
  app: 'Apps',
  appCategory: 'App categories',
  auditLog: 'Audit log',
  shopEvent: 'Shop events',
  plan: 'Plans',
  promotion: 'Promotions',
  revenue: 'Revenue',
  role: 'Roles',
  shop: 'Shops',
  shopifyFunction: 'Shopify Functions',
  subscription: 'Subscriptions',
  translation: 'Translations',
  widget: 'Widgets',
}

const ACTION_LABELS: Record<string, string> = {
  view: 'View',
  edit: 'Edit',
  delete: 'Delete',
  publish: 'Publish',
  credentialView: 'Read credential',
  activate: 'Activate',
  editDefault: 'Edit default',
}

export interface PermissionAction {
  readonly name: string
  readonly label: string
  readonly highRisk: boolean
}

export interface PermissionModule {
  readonly key: string
  readonly label: string
  readonly actions: readonly PermissionAction[]
}

/** The catalogue, grouped and labelled, in the order the modules are declared above. */
export const PERMISSION_MODULES: readonly PermissionModule[] = Object.entries(PERMISSIONS).map(
  ([key, group]) => ({
    key,
    label: MODULE_LABELS[key as keyof typeof PERMISSIONS],
    actions: Object.entries(group).map(([action, name]) => ({
      name,
      label: ACTION_LABELS[action] ?? action,
      highRisk: HIGH_RISK_PERMISSIONS.includes(name),
    })),
  }),
)
